import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import * as portalPasswordService from '../services/portalPasswordService';

/**
 * Storefront-branded "forgot your password?" entry point. Posts the
 * username through crm-api → login. The server always returns ok to
 * avoid leaking which usernames exist; we mirror that on the UI by
 * showing a "check your email" confirmation regardless of outcome.
 */
export function ForgotPasswordPage(): JSX.Element {
	const { t } = useTranslation();
	const [username, setUsername] = useState('');
	const [submitted, setSubmitted] = useState(false);
	const [submitting, setSubmitting] = useState(false);

	const handleSubmit = async (
		e: React.FormEvent<HTMLFormElement>,
	): Promise<void> => {
		e.preventDefault();
		if (username.trim().length === 0) return;
		setSubmitting(true);
		try {
			await portalPasswordService.forgotPassword(username.trim());
		} catch {
			// Swallow — login already collapses errors into ok=1 to prevent
			// timing-attack enumeration; we mirror that policy here.
		}
		setSubmitting(false);
		setSubmitted(true);
	};

	return (
		<div className="min-h-dvh flex items-center justify-center px-4 py-8 bg-brand-page-bg">
			<div className="w-full max-w-md bg-brand-surface border border-brand-border rounded-xl p-6 space-y-4">
				{submitted ? (
					<>
						<h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
							{t('Check your email')}
						</h1>
						<p className="text-sm text-gray-600 dark:text-gray-400">
							{t(
								'If an account matches that email or phone number, we just sent a link to reset your password. The link expires in one hour.',
							)}
						</p>
						<Link
							to="/"
							className="inline-block text-sm text-primary-600 hover:text-primary-700"
						>
							{t('Back to sign in')}
						</Link>
					</>
				) : (
					<form onSubmit={handleSubmit} className="space-y-4">
						<div>
							<h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
								{t('Reset your password')}
							</h1>
							<p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
								{t(
									'Enter the email or phone number on your account and we will send a reset link.',
								)}
							</p>
						</div>
						<label className="block">
							<span className="text-sm font-medium text-gray-700 dark:text-gray-200">
								{t('Email or phone')}
							</span>
							<input
								type="text"
								autoComplete="username"
								value={username}
								onChange={(e) => setUsername(e.target.value)}
								className="mt-1 block w-full rounded-md border border-brand-border bg-brand-input px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-portal-accent"
								required
							/>
						</label>
						<button
							type="submit"
							disabled={submitting || username.trim().length === 0}
							className="w-full rounded-md bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium py-2 transition-colors"
						>
							{submitting ? t('Sending...') : t('Send reset link')}
						</button>
						<div className="text-sm text-center">
							<Link to="/" className="text-primary-600 hover:text-primary-700">
								{t('Back to sign in')}
							</Link>
						</div>
					</form>
				)}
			</div>
		</div>
	);
}
